import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import "./paperMaster.css";
// import NavigationPaper from "../navbar/NavbarPaper";
import NavigationPaperDashboard from "../navbar/NavbarPaperDashboard";
import Alert from "../Alert/Alert";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import API_ENDPOINTS from "../../config/config";

const API_BASE_URL = API_ENDPOINTS.PAPER;

const PaperMasterForm = ({
  onSave,
  initialData,
  isEditing,
  onUpdate,
  onCancel,
  showAlert,
}) => {
  const initialFormState = useMemo(
    () => ({
      type: "K",
      reelSize: "",
      gsm: "",
      bf: "",
      partName: "",
    }),
    []
  );

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  useEffect(() => {
    if (isEditing && initialData) {
      // Transform backend data structure to match frontend
      setFormData({
        id: initialData.id,
        type: initialData.type || "K",
        reelSize: initialData.reel_size || "", // Match the backend field name
        gsm: initialData.gsm || "",
        bf: initialData.bf || "",
        partNumber: initialData.part_number || "",
        partName: initialData.part_name || "",
      });
    } else {
      setFormData(initialFormState);
    }
  }, [isEditing, initialData, initialFormState]);

  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case "reelSize":
        if (value && (isNaN(value) || value < 1 || value > 100)) {
          newErrors[name] = "Reel size must be between 1 and 100";
        } else {
          delete newErrors[name];
        }
        break;
      case "gsm":
        if (value && (isNaN(value) || value < 1 || value > 1000)) {
          newErrors[name] = "GSM must be between 1 and 1000";
        } else {
          delete newErrors[name];
        }
        break;
      case "bf":
        if (value && (isNaN(value) || value < 1 || value > 1000)) {
          newErrors[name] = "BF must be between 1 and 1000";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };

      if (name === "reelSize" || name === "gsm" || name === "bf") {
        if (newData.reelSize && newData.gsm && newData.bf) {
          newData.partName = `${newData.reelSize}/${newData.gsm}/${newData.bf}`;
        }
      }

      return newData;
    });
    validateField(name, value);
  };

  const handleSave = async () => {
    const isValid = ["reelSize", "gsm", "bf"].every((field) =>
      validateField(field, formData[field])
    );

    if (!isValid || !formData.reelSize || !formData.gsm || !formData.bf) {
      showAlert("Please fill in all required fields correctly", "error");
      return;
    }

    // Generate part name if not already set
    const partName = formData.partName || `${formData.reelSize}/${formData.gsm}/${formData.bf}`;

    try {
      // Transform camelCase to snake_case for backend
      const backendData = {
        type: formData.type,
        reel_size: formData.reelSize,
        gsm: formData.gsm,
        bf: formData.bf,
        part_name: partName,
      };

      console.log('Sending to backend:', backendData); // Debug log

      if (isEditing) {
        // Include id and part_number for updates
        backendData.id = formData.id;
        backendData.part_number = formData.partNumber;
        
        const response = await axios.put(
          `${API_BASE_URL}/update.php`,
          backendData
        );
        onUpdate(response.data);
      } else {
        // Don't send part_number for creation - backend will generate it
        const response = await axios.post(
          `${API_BASE_URL}/create.php`,
          backendData
        );
        if (response.data.id) {
          onSave(response.data);
        } else {
          throw new Error(
            response.data.message || "Failed to create paper master"
          );
        }
      }
      showAlert(
        `Paper Master ${isEditing ? "updated" : "saved"} successfully!`,
        "success"
      );
      onCancel();
    } catch (error) {
      let errorMessage = error.response?.data?.message || error.message;
      
      // Handle duplicate entry error specifically
      if (error.response?.status === 400) {
        if (errorMessage.toLowerCase().includes('duplicate') || 
            errorMessage.toLowerCase().includes('already exists')) {
          errorMessage = `Duplicate entry: ${formData.partName} already exists!`;
        }
      }
      
      showAlert(errorMessage, "error");
    }
  };

  const handleCancel = () => {
    setFormData(initialFormState);
    setErrors({});
    onCancel();
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-lg mx-auto mt-16 paper-box shade-form-card">
      <h2 className="text-2xl font-bold mb-6 card-header-paper heading-paper-master">
        {isEditing ? "Update Paper Master" : "Add Paper Master"}
      </h2>
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <label className="w-32 font-semibold input-fiel-name-paper1">
            Reel Size
          </label>
          <input
            type="number"
            name="reelSize"
            value={formData.reelSize}
            onChange={handleInputChange}
            className="border rounded px-3 py-2 w-32 input-text-paper"
          />
          {errors.reelSize && (
            <span className="text-red-500 text-sm">{errors.reelSize}</span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <label className="w-32 font-semibold input-fiel-name-paper2">
            GSM
          </label>
          <input
            type="number"
            name="gsm"
            value={formData.gsm}
            onChange={handleInputChange}
            className="border rounded px-3 py-2 w-32 input-text-paper"
          />
          {errors.gsm && (
            <span className="text-red-500 text-sm">{errors.gsm}</span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <label className="w-32 font-semibold input-fiel-name-paper3">
            BF
          </label>
          <input
            type="number"
            name="bf"
            value={formData.bf}
            onChange={handleInputChange}
            className="border rounded px-3 py-2 w-32 input-text-paper"
          />
          {errors.bf && (
            <span className="text-red-500 text-sm">{errors.bf}</span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <label className="w-32 font-semibold input-fiel-name-paper5">
            Paper Name
          </label>
          <input
            type="text"
            value={formData.partName}
            disabled
            className="border rounded px-3 py-2 w-full bg-gray-100 input-text-paper"
          />
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100 button-cancel"
          >
            Clear
          </button>
          <button
            onClick={handleSave}
            disabled={Object.keys(errors).length > 0}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300 button-save"
          >
            {isEditing ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};


const PaperMasterTable = ({ data, onEdit, onDelete, onSearch, onFilter }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchName, setSearchName] = useState("");
  const [filters, setFilters] = useState({
    reelSize: "",
    gsm: "",
    bf: "",
  });
  const itemsPerPage = 15;

  // Reset function for clearing filters
  const resetFilters = () => {
    setSearchName("");
    setFilters({
      reelSize: "",
      gsm: "",
      bf: "",
    });
    setSortColumn(null);
    setSortDirection('asc');
    onSearch("");
    onFilter({
      reelSize: "",
      gsm: "",
      bf: "",
    });
    setCurrentPage(1);
  };

  // Reset to page 1 when search filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchName, filters]);

  const handleSearchChange = (value) => {
    setSearchName(value);
    onSearch(value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    onFilter(newFilters);
  };

  // Sorting and filtering function
  const processPapers = () => {
    let processedData = [...data];

    // Sort if a column is selected
    if (sortColumn) {
      processedData.sort((a, b) => {
        let valueA = a[sortColumn];
        let valueB = b[sortColumn];

        // Handle string comparison for part_name
        if (sortColumn === 'part_name') {
          valueA = valueA.toLowerCase();
          valueB = valueB.toLowerCase();
        }

        // Handle numeric comparison for other columns
        if (typeof valueA === 'number' || !isNaN(Number(valueA))) {
          valueA = Number(valueA);
          valueB = Number(valueB);
        }

        if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return processedData;
  };

  // Sorting handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      // If already sorting this column, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If new column, start with ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sorting icon component
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return (
        <span className="ml-2 text-gray-300">
          ↕️
        </span>
      );
    }
    return sortDirection === 'asc' ? '⬆️' : '⬇️';
  };

  // Apply sorting and pagination
  const filteredPapers = processPapers();
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPapers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPapers.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="mt-8 filter-search-container">
      <div className="flex flex-col">
        <div className="flex items-center justify-between">
          <div className="mb-4 flex gap-4 search-container">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search by Paper Name..."
                value={searchName}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="border rounded px-3 py-2 w-full max-w-md focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div className="flex space-x-4 filter-container">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium">Reel Size:</label>
                <input
                  type="number"
                  name="reelSize"
                  className="border rounded px-3 py-2 w-24"
                  value={filters.reelSize}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium">GSM:</label>
                <input
                  type="number"
                  name="gsm"
                  className="border rounded px-3 py-2 w-24"
                  value={filters.gsm}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium">BF:</label>
                <input
                  type="number"
                  name="bf"
                  className="border rounded px-3 py-2 w-24"
                  value={filters.bf}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
          </div>
          
          <button
            onClick={resetFilters}
            className="px-4 py-2 text-sm reset"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="table-container">
        <div className="table-wrapper">
          <table className="shade-table">
            <thead>
              <tr className="bg-gray-50">
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('part_name')}
                  style={{ width: '200px' }}
                >
                  Paper Name (ReelSz/GSM/BF)
                  <SortIcon column="part_name" />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('reel_size')}
                  style={{ width: '120px' }}
                >
                  Reel Size
                  <SortIcon column="reel_size" />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('gsm')}
                  style={{ width: '100px' }}
                >
                  GSM
                  <SortIcon column="gsm" />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('bf')}
                  style={{ width: '100px' }}
                >
                  BF
                  <SortIcon column="bf" />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('part_number')}
                  style={{ width: '120px' }}
                >
                  Part Number
                  <SortIcon column="part_number" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '180px' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((paper) => (
                <tr key={paper.id}>
                  <td className="px-6 py-4 whitespace-nowrap" style={{ width: '200px' }}>
                    {paper.part_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" style={{ width: '120px' }}>
                    {paper.reel_size}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" style={{ width: '100px' }}>{paper.gsm}</td>
                  <td className="px-6 py-4 whitespace-nowrap" style={{ width: '100px' }}>{paper.bf}</td>
                  <td className="px-6 py-4 whitespace-nowrap" style={{ width: '120px' }}>
                    {paper.part_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" style={{ width: '180px' }}>
                    <div className="flex space-x-2 actions">
                      <button
                        className="text-blue-600 hover:text-blue-900 mr-4 btn-update"
                        onClick={() => onEdit(paper)}
                      >
                        Update
                      </button>
                      <button
                        className="text-red-600 hover:text-red-900 btn-delete"
                        onClick={() => onDelete(paper)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Controls */}
          <div className="pagination-controls">
            <div className="flex items-center justify-center space-x-6">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
                title="Previous Page"
              >
                ←
              </button>

              {/* Page Info */}
              <div className="pagination-info">
                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
              </div>

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
                title="Next Page"
              >
                →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaperMasterFind = () => {
  const [paperMasters, setPaperMasters] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    reelSize: "",
    gsm: "",
    bf: "",
  });
  const [editingPaper, setEditingPaper] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    paperId: null,
    paperName: ""
  });

  const showAlert = (message, type = "info") => {
    setAlert({ message, type });
  };

  // Fetch paper masters on component mount
  useEffect(() => {
    fetchPaperMasters();
  }, []);

  const fetchPaperMasters = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/read.php`);
      setPaperMasters(response.data);
      setFilteredData(response.data);
    } catch (error) {
      showAlert(
        `Error fetching Paper Masters: ${
          error.response?.data?.message || error.message
        }`,
        "error"
      );
    }
  };

  const filterData = useCallback(() => {
    let filtered = [...paperMasters];

    if (searchTerm) {
      filtered = filtered.filter((paper) =>
        paper.part_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filters.reelSize) {
      filtered = filtered.filter(
        (paper) =>
          paper.reel_size && paper.reel_size.toString() === filters.reelSize
      );
    }

    if (filters.gsm) {
      filtered = filtered.filter(
        (paper) => paper.gsm.toString() === filters.gsm
      );
    }
    if (filters.bf) {
      filtered = filtered.filter((paper) => paper.bf.toString() === filters.bf);
    }

    setFilteredData(filtered);
  }, [searchTerm, filters, paperMasters]);

  // useEffect(() => {
  //   filterData();
  // }, [filterData]);

  const handleSave = (formData) => {
    setPaperMasters((prev) => [...prev, formData]);
    setFilteredData((prev) => [...prev, formData]);
    showAlert("Paper Master added successfully!", "success");
  };
  const handleUpdate = (updatedPaper) => {
    setPaperMasters((prev) =>
      prev.map((paper) => (paper.id === updatedPaper.id ? updatedPaper : paper))
    );
    setFilteredData((prev) =>
      prev.map((paper) => (paper.id === updatedPaper.id ? updatedPaper : paper))
    );
    setEditingPaper(null);
    showAlert("Paper Master updated successfully!", "success");
  };

  const handleDeleteClick = (paper) => {
    setConfirmModal({
      isOpen: true,
      paperId: paper.id,
      paperName: paper.part_name
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/delete.php?id=${confirmModal.paperId}`);
      setPaperMasters((prev) => prev.filter((paper) => paper.id !== confirmModal.paperId));
      setFilteredData((prev) => prev.filter((paper) => paper.id !== confirmModal.paperId));
      showAlert("Paper Master deleted successfully!", "success");
    } catch (error) {
      showAlert(
        `Error deleting Paper Master: ${
          error.response?.data?.message || error.message
        }`,
        "error"
      );
    }
  };

  const handleDeleteCancel = () => {
    setConfirmModal({
      isOpen: false,
      paperId: null,
      paperName: ""
    });
  };

  const handleSearch = async (term) => {
    try {
      setSearchTerm(term);
      // Use empty filters if term is empty (reset scenario)
      const currentFilters = term === "" ? { reelSize: "", gsm: "", bf: "" } : filters;
      applyFilters(term, currentFilters);
    } catch (error) {
      showAlert(`Error searching Paper Masters: ${error.message}`, "error");
    }
  };

  const handleFilter = async (newFilters) => {
    setFilters(newFilters);
    try {
      // Use empty searchTerm if all filters are empty (reset scenario)
      const allFiltersEmpty = !newFilters.reelSize && !newFilters.gsm && !newFilters.bf;
      const currentTerm = allFiltersEmpty ? "" : searchTerm;
      applyFilters(currentTerm, newFilters);
    } catch (error) {
      showAlert(`Error filtering Paper Masters: ${error.message}`, "error");
    }
  };

  const applyFilters = (term, currentFilters) => {
    // Filter locally for partial matches
    let filtered = paperMasters;
    
    if (term) {
      filtered = filtered.filter(paper =>
        paper.part_name?.toLowerCase().includes(term.toLowerCase())
      );
    }

    if (currentFilters.reelSize) {
      filtered = filtered.filter(paper =>
        paper.reel_size?.toString().includes(currentFilters.reelSize)
      );
    }

    if (currentFilters.gsm) {
      filtered = filtered.filter(paper =>
        paper.gsm?.toString().includes(currentFilters.gsm)
      );
    }

    if (currentFilters.bf) {
      filtered = filtered.filter(paper =>
        paper.bf?.toString().includes(currentFilters.bf)
      );
    }

    setFilteredData(filtered);
  };


  const handleEdit = (paper) => {
    setEditingPaper(paper);
    // setShowForm(true);
  };

  const handleCancel = () => {
    setEditingPaper(null);
    // setShowForm(false);
  };

  return (
    <>
      <NavigationPaperDashboard />
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
      
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Paper Master"
        message={`Are you sure you want to delete "${confirmModal.paperName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
      
      <div className="paper-master-container">
        <div className="content-layout">
          <div className="form-section">
            <PaperMasterForm
              onSave={handleSave}
              initialData={editingPaper}
              isEditing={!!editingPaper}
              onUpdate={handleUpdate}
              onCancel={handleCancel}
              showAlert={showAlert}
            />
          </div>

          <PaperMasterTable
            data={filteredData}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            onSearch={handleSearch}
            onFilter={handleFilter}
          />
        </div>
      </div>
    </>
  );
};

export default PaperMasterFind;
